import { requestBuilder } from '../../../http/RequestBuilder';

/**
 * GET …/fruitful-ai/transactions/count-by-connection-account
 * Response shape: AiServiceTransactionCountsByConnectionAccountResponse (parsed defensively).
 */
const EMPTY_COUNTS: Record<string, number> = {};

const tryAddCount = (
    map: Map<string, number>,
    accountId: unknown,
    count: unknown,
) => {
    if (typeof accountId !== 'string' || accountId === '') {
        return;
    }
    if (typeof count !== 'number' || !Number.isFinite(count)) {
        return;
    }
    map.set(accountId, count);
};

const addCountsFromArray = (map: Map<string, number>, arr: unknown[]) => {
    for (const item of arr) {
        if (item == null || typeof item !== 'object') {
            continue;
        }
        const o = item as Record<string, unknown>;
        const id =
            (typeof o.connectionAccountId === 'string' &&
                o.connectionAccountId) ||
            (typeof o.connection_account_id === 'string' &&
                o.connection_account_id) ||
            (typeof o.accountId === 'string' && o.accountId) ||
            (typeof o.account_id === 'string' && o.account_id) ||
            (typeof o.id === 'string' && o.id) ||
            undefined;
        const count =
            o.count ??
            o.transactionCount ??
            o.transaction_count ??
            o.total ??
            o.totalCount;
        tryAddCount(map, id, count);
    }
};

const addCountsFromNumberRecord = (
    map: Map<string, number>,
    rec: Record<string, unknown>,
) => {
    for (const [k, v] of Object.entries(rec)) {
        tryAddCount(map, k, v);
    }
};

const isNumberValuesRecord = (rec: Record<string, unknown>): boolean => {
    const vals = Object.values(rec);
    return (
        vals.length > 0 &&
        vals.every((v) => typeof v === 'number' && Number.isFinite(v))
    );
};

export const parseTransactionCountsByConnectionAccountPayload = (
    raw: unknown,
): Record<string, number> => {
    const map = new Map<string, number>();
    if (raw == null || raw === '') {
        return EMPTY_COUNTS;
    }

    let data: unknown = raw;
    if (
        typeof data === 'object' &&
        data !== null &&
        'data' in data &&
        (data as { data: unknown }).data != null
    ) {
        data = (data as { data: unknown }).data;
    }

    if (Array.isArray(data)) {
        addCountsFromArray(map, data);
        return map.size === 0 ? EMPTY_COUNTS : Object.fromEntries(map);
    }

    if (typeof data !== 'object' || data === null) {
        return EMPTY_COUNTS;
    }

    const o = data as Record<string, unknown>;

    for (const key of [
        'counts',
        'accounts',
        'connectionAccounts',
        'byAccount',
        'items',
    ] as const) {
        const v = o[key];
        if (Array.isArray(v)) {
            addCountsFromArray(map, v);
            if (map.size > 0) {
                return Object.fromEntries(map);
            }
        } else if (
            v != null &&
            typeof v === 'object' &&
            !Array.isArray(v) &&
            isNumberValuesRecord(v as Record<string, unknown>)
        ) {
            addCountsFromNumberRecord(map, v as Record<string, unknown>);
            if (map.size > 0) {
                return Object.fromEntries(map);
            }
        }
    }

    if (isNumberValuesRecord(o)) {
        addCountsFromNumberRecord(map, o);
        return map.size === 0 ? EMPTY_COUNTS : Object.fromEntries(map);
    }

    for (const key of [
        'countsByConnectionAccount',
        'countsByConnectionAccountId',
        'byConnectionAccountId',
        'countByConnectionAccount',
    ] as const) {
        const v = o[key];
        if (v != null && typeof v === 'object' && !Array.isArray(v)) {
            addCountsFromNumberRecord(map, v as Record<string, unknown>);
            if (map.size > 0) {
                return Object.fromEntries(map);
            }
        }
    }

    return map.size === 0 ? EMPTY_COUNTS : Object.fromEntries(map);
};

export const getFruitfulAiTransactionCountsQueryKey = (
    membershipId: string,
): string =>
    `getFruitfulAiTransactionCountsByConnectionAccount/${membershipId}`;

const useGetFruitfulAiTransactionCountsByConnectionAccount =
    requestBuilder.query<
        unknown,
        { membershipId: string },
        Error,
        Record<string, number>
    >({
        endpoint: ({ membershipId }) =>
            `/api/admin/memberships/${membershipId}/fruitful-ai/transactions/count-by-connection-account`,
        queryKey: ({ membershipId }) =>
            getFruitfulAiTransactionCountsQueryKey(membershipId),
    });

export const useFruitfulAiTransactionCountsByConnectionAccount = (
    membershipId: string | null,
    options?: { enabled?: boolean },
): {
    trxCountByConnectionAccountId: Record<string, number>;
    isLoadingTrxCounts: boolean;
    isTrxCountsError: boolean;
} => {
    const userEnabled = options?.enabled ?? true;

    const { data, isLoading, isError } =
        useGetFruitfulAiTransactionCountsByConnectionAccount(
            { membershipId: membershipId ?? '' },
            {
                enabled:
                    userEnabled && membershipId != null && membershipId !== '',
                select: (raw) =>
                    parseTransactionCountsByConnectionAccountPayload(raw),
                staleTime: 30_000,
            },
        );

    return {
        trxCountByConnectionAccountId: data ?? EMPTY_COUNTS,
        isLoadingTrxCounts: isLoading,
        isTrxCountsError: isError,
    };
};
